// ** Type import
import { signOut } from '@erikdakoda/auth/client/betterAuthClient';
import type { HorizontalNavItemsType } from '@erikdakoda/site-template-ui/types';
import type { VerticalNavItemsType } from '@erikdakoda/site-template-ui/types';
import {
  getDefaultSyncDatabaseSourceLabel,
  getSyncDatabaseMenuTitle,
  isLocalDevelopmentEnvironment,
  neonDbEnvVarToLabel,
  type DbSyncEnvParams,
} from '@erikdakoda/database/shared/neonDbEnvConfig';

const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV;
const defaultSyncSource = getDefaultSyncDatabaseSourceLabel(vercelEnv);

/** Confirms and sends the admin sync-database Inngest event. */
function confirmSyncDatabase(params: DbSyncEnvParams = {}): void {
  const source = params.remoteEnv ? neonDbEnvVarToLabel(params.remoteEnv) : defaultSyncSource;
  if (window.confirm(`Sync DB from ${source}? This will overwrite existing configurations.`)) {
    void fetch('/api/send-inngest-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: 'admin/sync-database', data: params }),
    });
  }
}

const syncDatabaseNavItems: HorizontalNavItemsType = [
  {
    title: getSyncDatabaseMenuTitle(defaultSyncSource),
    icon: 'light-database-rotate',
    roles: ['ADMIN', 'TEAM'],
    onClick: () => confirmSyncDatabase(),
  },
  ...(isLocalDevelopmentEnvironment(vercelEnv)
    ? [
        {
          title: getSyncDatabaseMenuTitle('Prev'),
          icon: 'light-database-rotate',
          roles: ['ADMIN', 'TEAM'],
          onClick: () => confirmSyncDatabase({ remoteEnv: 'NEON_URL_PREV', fromMaster: false }),
        },
      ]
    : []),
];

const navItems = [
  {
    title: 'Agent Chat',
    path: '/',
    icon: 'message-lines',
    roles: ['USER', 'ADMIN', 'TEAM'],
  },
  {
    title: 'Bot Craft',
    path: '/bot-craft',
    icon: 'robot',
    roles: ['USER', 'ADMIN', 'TEAM'],
  },
  {
    title: 'Admin',
    icon: 'screwdriver-wrench',
    roles: ['ADMIN', 'TEAM'],
    children: [
      {
        title: 'Executions',
        path: '/admin/executions',
        icon: 'conveyor-belt-boxes',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Forge Projects',
        path: '/admin/forge-projects',
        icon: 'chart-gantt',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Repo Packages',
        path: '/admin/repo-packages',
        icon: 'cubes',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Sentry Issues',
        path: '/admin/sentry-issues',
        icon: 'triangle-exclamation',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Admin Actions',
        path: '/admin/admin-actions',
        icon: 'server',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Public Env Vars',
        path: '/admin/public-env',
        icon: 'code',
        roles: ['ADMIN', 'TEAM'],
      },
      ...syncDatabaseNavItems,
    ],
  },
  {
    title: 'AI',
    icon: 'hexagon-nodes',
    roles: ['ADMIN', 'TEAM'],
    children: [
      {
        title: 'Bots',
        path: '/admin/bots',
        icon: 'robot',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Prompts',
        path: '/admin/prompts',
        icon: 'square-terminal',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Compare Bots',
        path: '/admin/compare-bots',
        icon: 'light-robot-sync',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Compare Bots with Prev',
        path: '/admin/compare-bots-with-prev',
        icon: 'light-robot-sync',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'AI Models',
        path: '/admin/ai-models',
        icon: 'hexagon-nodes',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Model Scores',
        path: '/admin/model-scores',
        icon: 'chart-simple',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'AI Tools',
        path: '/admin/ai-tools',
        icon: 'hammer',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'MCP Servers',
        path: '/admin/mcp-servers',
        icon: 'light-mcp',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Tool Sets',
        path: '/admin/tool-sets',
        icon: 'toolbox',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Agent Convos',
        path: '/admin/agent-convos',
        icon: 'message-lines',
        roles: ['ADMIN', 'TEAM'],
      },
    ],
  },
  {
    title: 'Users',
    icon: 'users',
    roles: ['ADMIN', 'TEAM'],
    children: [
      {
        title: 'Users',
        path: '/admin/users',
        icon: 'users',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Convos',
        path: '/admin/convos',
        icon: 'message-lines',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Factoids',
        path: '/admin/factoids',
        icon: 'head-side-brain',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Todos',
        path: '/admin/todo',
        icon: 'clipboard-check',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Notifications',
        path: '/admin/notifications',
        icon: 'bell',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Feedback',
        path: '/admin/feedback',
        icon: 'bullhorn',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Ratings',
        path: '/admin/ratings',
        icon: 'thumbs-up',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Settings',
        path: '/admin/settings',
        icon: 'square-sliders',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Setting Templates',
        path: '/admin/setting-templates',
        icon: 'square-sliders',
        roles: ['ADMIN'],
      },
      {
        title: 'Connections',
        path: '/admin/connections',
        icon: 'puzzle-piece',
        roles: ['ADMIN', 'TEAM'],
      },
    ],
  },
  {
    title: 'Test pages',
    icon: 'palette',
    roles: ['ADMIN', 'TEAM'],
    children: [
      {
        title: 'Theme',
        path: '/theme',
        icon: 'palette',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Markdown Editor',
        path: '/admin/markdown-editor',
        icon: 'light-markdown-book-pen',
        roles: ['USER', 'ADMIN'],
      },
      {
        title: 'AI Test Chat',
        path: '/admin/ai-test-chat',
        icon: 'message-lines',
        roles: ['ADMIN', 'TEAM'],
      },
    ],
  },
  {
    title: 'User settings',
    path: '/user/settings/security',
    icon: 'gear-complex',
    roles: ['USER', 'ADMIN', 'TEAM'],
  },
  {
    title: 'Log out',
    icon: 'right-from-bracket',
    roles: ['USER', 'ADMIN', 'TEAM'],
    onClick: async () => {
      await signOut();
    },
  },
  {
    title: 'Log in',
    path: 'login',
    icon: 'right-from-bracket',
    roles: ['GUEST'],
  },
  {
    title: 'Sign up',
    path: 'signup',
    icon: 'user-plus',
    roles: ['GUEST'],
  },
];

export const HorizontalNavItems = () => navItems as HorizontalNavItemsType;

export const VerticalNavItems = () => navItems as VerticalNavItemsType;
